import {
  ArrowLongRightIcon,
  ArrowTurnDownRightIcon,
  ArrowTurnUpRightIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "react-h5-audio-player/lib/styles.css";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import Tooltip from "../common/Tooltip";
import Skeleton from "react-loading-skeleton";

interface SnippetsProps {
  onInsertContent?: (content: string, mode?: string) => void;
}

interface Snippet {
  id: string;
  title: string;
  content: string;
  description?: string;
}

function stylePlaceholders(text: string): string {
  // Wrap {{placeholder}} text with compact styling for snippet previews
  // Hide the brackets using replace-text-bracket class
  return text.replace(
    /\{\{([^}]+)\}\}/g,
    '<span class="replace-text-bracket">{{</span><span class="replace-text-compact">$1</span><span class="replace-text-bracket">}}</span>'
  );
}

function Snippets({ onInsertContent }: SnippetsProps) {
  const [userSnippets, setUserSnippets] = useState<Snippet[]>([]);
  const [agencySnippets, setAgencySnippets] = useState<Snippet[]>([]);
  const [newSnippet, setNewSnippet] = useState({
    title: "",
    content: "",
    description: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<Snippet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { checkAuthStatus } = useAuth();

  useEffect(() => {
    fetchSnippets();
  }, []);

  const formatSnippet = (snippet: string) => {
    return snippet
      .replace(/\n/g, "</p><p>")
      .replace(/<p><\/p>/g, "")
      .trim();
  };

  const fetchSnippets = async () => {
    try {
      const response = await axios.get("/api/snippets", {
        withCredentials: true,
      });
      setUserSnippets(response.data.snippets);
      setAgencySnippets(response.data.agencySnippets || []);
    } catch (error) {
      checkAuthStatus();
      toast.error("Failed to fetch snippets");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSnippet = async () => {
    try {
      const response = await axios.post("/api/snippets", newSnippet, {
        withCredentials: true,
      });
      setUserSnippets([...userSnippets, response.data.snippet]);
      closeDialog();
      toast.success("Snippet added successfully");
    } catch (error) {
      toast.error("Failed to add snippet");
    }
  };

  const handleDeleteSnippet = async (id: string) => {
    try {
      await axios.delete(`/api/snippets/${id}`, {
        withCredentials: true,
      });
      setUserSnippets(userSnippets.filter((snippet) => snippet.id !== id));
      toast.success("Snippet deleted successfully");
    } catch (error) {
      toast.error("Failed to delete snippet");
    }
  };

  const handleEditSnippet = async (id: string) => {
    const snippet = userSnippets.find((s) => s.id === id);
    if (snippet) {
      setEditingSnippet(snippet);
      setNewSnippet({
        title: snippet.title,
        content: snippet.content,
        description: snippet.description || "",
      });
      setIsEditMode(true);
      openDialog();
    }
  };

  const handleSaveEdit = async () => {
    if (!editingSnippet) return;

    try {
      const response = await axios.patch(
        `/api/snippets/${editingSnippet.id}`,
        newSnippet,
        { withCredentials: true }
      );

      setUserSnippets(
        userSnippets.map((snippet) =>
          snippet.id === editingSnippet.id ? response.data.snippet : snippet
        )
      );

      closeDialog();
      toast.success("Snippet updated successfully");
    } catch (error) {
      toast.error("Failed to update snippet");
    }
  };

  const handleInsertContent = (content: string, mode?: string) => {
    if (onInsertContent) {
      onInsertContent(formatSnippet(content), mode);
      toast.success("Added to report");
    }
  };

  const openDialog = () => {
    dialogRef.current?.showModal();
  };

  const closeDialog = () => {
    dialogRef.current?.close();
    setIsEditMode(false);
    setEditingSnippet(null);
    setNewSnippet({ title: "", content: "", description: "" });
  };

  const SnippetsSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 bg-base-200 rounded-lg">
          <Skeleton height={20} width="40%" className="mb-2" />
          <Skeleton height={36} />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="pt-2 md:pt-0" data-tour="snippets-panel">
        <div className="flex items-center prose mb-2">
          <ClipboardDocumentListIcon className="size-5 text-blue-500 dark:text-blue-300" />
          <h3 className="m-0 ml-2 text-base">Snippets</h3>
        </div>
        <div className="grid gap-y-2">
          {isLoading ? (
            <SnippetsSkeleton />
          ) : (
            <>
              {agencySnippets.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-300 uppercase tracking-wide mt-1">
                    Department Snippets
                  </p>
                  {agencySnippets.map((snippet) => (
                    <Tooltip
                      key={snippet.id}
                      placement="top"
                      overlay={stylePlaceholders(snippet.description || "")}
                    >
                      <div className="dropdown dropdown-end" data-tour="snippet-item" data-snippet-scope="department">
                        <div
                          tabIndex={0}
                          className="p-4 text-sm cursor-pointer transition duration-100 border-b-2 bg-base-200 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/40 border-transparent"
                        >
                          <h4
                            className="font-semibold"
                            dangerouslySetInnerHTML={{
                              __html: stylePlaceholders(snippet.title),
                            }}
                          />
                          <p
                            className="text-black-300 dark:text-base-content/70 line-clamp-2"
                            dangerouslySetInnerHTML={{
                              __html: stylePlaceholders(snippet.content),
                            }}
                          />
                        </div>
                        <ul
                          tabIndex={0}
                          className="dropdown-content menu bg-base-100 rounded-lg z-20 p-2 shadow mt-2 w-full"
                        >
                          <li>
                            <p
                              className="hover:bg-gray-100 dark:hover:bg-base-200"
                              onClick={() =>
                                handleInsertContent(snippet.content, "cursor")
                              }
                            >
                              <ArrowLongRightIcon className="size-4" />
                              Add to cursor
                            </p>
                          </li>
                          <li>
                            <p
                              className="hover:bg-gray-100 dark:hover:bg-base-200"
                              onClick={() =>
                                handleInsertContent(snippet.content, "top")
                              }
                            >
                              <ArrowTurnUpRightIcon className="size-4" />
                              Add to top of report
                            </p>
                          </li>
                          <li>
                            <p
                              className="hover:bg-gray-100 dark:hover:bg-base-200"
                              onClick={() =>
                                handleInsertContent(snippet.content, "end")
                              }
                            >
                              <ArrowTurnDownRightIcon className="size-4" />
                              Add to end of report
                            </p>
                          </li>
                        </ul>
                      </div>
                    </Tooltip>
                  ))}
                </>
              )}

              {agencySnippets.length > 0 && (
                <p className="text-xs font-semibold text-base-content/60 uppercase tracking-wide mt-2">
                  My Snippets
                </p>
              )}

              {userSnippets.length > 0 ? (
                userSnippets.map((snippet) => (
                  <Tooltip
                    key={snippet.id}
                    placement="top"
                    overlay={stylePlaceholders(snippet.description || "")}
                  >
                    <div className="dropdown dropdown-end" data-tour="snippet-item" data-snippet-scope="mine">
                      <div
                        tabIndex={0}
                        className="p-4 text-sm cursor-pointer transition duration-100 border-b-2 bg-base-200 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/40 border-transparent"
                      >
                        <h4
                          className="font-semibold"
                          dangerouslySetInnerHTML={{
                            __html: stylePlaceholders(snippet.title),
                          }}
                        />
                        <p
                          className="text-black-300 dark:text-base-content/70 line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: stylePlaceholders(snippet.content),
                          }}
                        />
                      </div>
                      <ul
                        tabIndex={0}
                        className="dropdown-content menu bg-base-100 rounded-lg z-20 p-2 shadow mt-2 w-full"
                      >
                        <li>
                          <p
                            className="hover:bg-gray-100 dark:hover:bg-base-200"
                            onClick={() =>
                              handleInsertContent(snippet.content, "cursor")
                            }
                          >
                            <ArrowLongRightIcon className="size-4" />
                            Add to cursor
                          </p>
                        </li>
                        <li>
                          <p
                            className="hover:bg-gray-100 dark:hover:bg-base-200"
                            onClick={() =>
                              handleInsertContent(snippet.content, "top")
                            }
                          >
                            <ArrowTurnUpRightIcon className="size-4" />
                            Add to top of report
                          </p>
                        </li>
                        <li>
                          <p
                            className="hover:bg-gray-100 dark:hover:bg-base-200"
                            onClick={() =>
                              handleInsertContent(snippet.content, "end")
                            }
                          >
                            <ArrowTurnDownRightIcon className="size-4" />
                            Add to end of report
                          </p>
                        </li>
                        <li>
                          <p
                            className="hover:bg-gray-100 dark:hover:bg-base-200"
                            onClick={() => handleEditSnippet(snippet.id)}
                          >
                            <PencilSquareIcon className="size-4" />
                            Edit snippet
                          </p>
                        </li>
                        <li>
                          <p
                            className="hover:bg-gray-100 dark:hover:bg-base-200"
                            onClick={() => handleDeleteSnippet(snippet.id)}
                          >
                            <TrashIcon className="size-4" />
                            Delete snippet
                          </p>
                        </li>
                      </ul>
                    </div>
                  </Tooltip>
                ))
              ) : (
                <p className="text-sm my-2">
                  Create reusable text blocks to speed up your reporting.
                </p>
              )}
            </>
          )}

          <Button variant="primary" className="w-full" onClick={openDialog} data-tour="snippet-add">
            Add Snippet
          </Button>
        </div>
      </div>

      <dialog ref={dialogRef} className="modal">
        <div className="modal-box" data-tour="snippet-dialog">
          <h3 className="font-bold text-lg">
            {isEditMode ? "Edit Snippet" : "Add New Snippet"}
          </h3>
          <p className="text-sm text-base-content/60 mt-2 mb-2">
            Use double braces <code className="bg-gray-100 dark:bg-base-200 px-1 rounded">&#123;&#123;placeholder&#125;&#125;</code> for fillable placeholders in Title and Content.
          </p>
          <div className="form-control">
            <h3 className="font-semibold text-sm mt-2 mb-1">Title</h3>
            <Input
              type="text"
              className="input input-bordered !max-w-full mb-2"
              value={newSnippet.title}
              onChange={(e) =>
                setNewSnippet({ ...newSnippet, title: e.target.value })
              }
            />
            <h3 className="font-semibold text-sm mt-2 mb-1">
              Description (optional)
            </h3>
            <Input
              type="text"
              className="input input-bordered !max-w-full mb-2"
              value={newSnippet.description}
              onChange={(e) =>
                setNewSnippet({ ...newSnippet, description: e.target.value })
              }
            />
            <h3 className="font-semibold text-sm mt-2 mb-1">Content</h3>
            <textarea
              className="textarea textarea-bordered focus:outline-none focus:border-blue-500 border-grey-500 dark:border-base-300 bg-grey-50 dark:bg-base-200 transition-colors !max-w-full"
              value={newSnippet.content}
              onChange={(e) =>
                setNewSnippet({ ...newSnippet, content: e.target.value })
              }
            />
          </div>
          <div className="modal-action">
            <Button variant="clean" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-tour="snippet-save"
              onClick={isEditMode ? handleSaveEdit : handleAddSnippet}
            >
              {isEditMode ? "Save" : "Add"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeDialog}>close</button>
        </form>
      </dialog>
    </>
  );
}

export default Snippets;
